import React, { useState, useEffect, useId } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { X, Cookie } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from './ui/button';
import { flag } from '../config';
import { useAuth } from '../context/AuthContext';
import { bottomNavShown } from './bottomNavRules';
import SiteButton from './site/Button';
import Eyebrow from './site/Eyebrow';
import TextLink from './site/TextLink';

/**
 * The visitor's choice, in localStorage under this key: 'accepted' or 'dismissed'. No value
 * = ask. App.js's GTMPageTracker pushes page views only while it is 'accepted' (audit row
 * 112), so both looks below read and write exactly this key and these two values.
 */
export const CONSENT_KEY = 'cookieConsent';

/** Shown until a choice is stored; accept / dismiss store it and hide the banner. */
function useConsentChoice() {
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Check if user has already accepted/dismissed the banner
    let consent = null;
    try {
      consent = localStorage.getItem(CONSENT_KEY);
    } catch {
      consent = null;
    }
    if (!consent) {
      setShowBanner(true);
    }
  }, []);

  const choose = (value) => {
    try {
      localStorage.setItem(CONSENT_KEY, value);
    } catch {
      // Storage blocked (private mode): hide for this page view anyway.
    }
    setShowBanner(false);
  };

  return {
    showBanner,
    handleAccept: () => choose('accepted'),
    handleDismiss: () => choose('dismissed'),
  };
}

/**
 * SLOT_MODEL (Sept 2026 redesign) look: a sheet panel with a rule border and a 4px orange
 * top edge, navy text, an IBM Plex Mono "Cookies" eyebrow, the same copy as before, a
 * "Learn more" link to /privacy, Accept (primary) and Dismiss (secondary) buttons, and the
 * close button from 800px. Fixed to the bottom of the viewport inside the 1440px canvas;
 * when the patron bottom nav is on screen (signed in, below 800px) it sits just above it.
 * A `.site-light` surface (rust focus rings). Same behaviour as the legacy banner: not a
 * dialog, focus is not moved, no other storage or events.
 */
function SiteConsentBanner({ onAccept, onDismiss }) {
  const titleId = `consent-title-${useId().replace(/:/g, '')}`;
  const { pathname } = useLocation();
  const { isAuthenticated } = useAuth() || {};
  const aboveNav = bottomNavShown(isAuthenticated, pathname);

  return (
    <div
      role="region"
      aria-labelledby={titleId}
      data-consent-banner=""
      data-above-nav={aboveNav ? 'true' : 'false'}
      className={cn(
        'site-light pointer-events-none fixed inset-x-0 z-[70] px-[15px] pb-[15px] motion-safe:animate-site-rise nav:px-6 nav:pb-6',
        aboveNav ? 'bottom-[calc(4rem+env(safe-area-inset-bottom,0px))] nav:bottom-0' : 'bottom-[env(safe-area-inset-bottom,0px)]',
      )}
    >
      <div className="pointer-events-auto mx-auto flex max-w-[73.5rem] flex-col gap-5 border border-t-4 border-brand-rule border-t-brand-orange bg-brand-sheet p-5 text-navy shadow-site-card nav:flex-row nav:items-center nav:gap-8 nav:px-7 nav:py-6">
        <div className="min-w-0 flex-1">
          <Eyebrow as="p" tone="rust" icon={<Cookie />} className="m-0">Cookies</Eyebrow>
          <p id={titleId} className="mb-1 mt-2 font-body text-base font-semibold leading-snug">
            We use cookies to enhance your experience
          </p>
          <p className="m-0 font-body text-sm leading-relaxed text-brand-muted">
            This site uses cookies for analytics and improving your experience. By continuing to use this site, you agree to our use of cookies.{' '}
            <TextLink to="/privacy" size="sm" className="py-0 text-navy">
              Learn more
            </TextLink>
          </p>
        </div>
        <div className="flex items-center gap-3">
          <SiteButton variant="secondary" size="sm" arrow={false} onClick={onDismiss} className="flex-1 nav:flex-none">
            Dismiss
          </SiteButton>
          <SiteButton size="sm" arrow={false} onClick={onAccept} className="flex-1 nav:flex-none">
            Accept
          </SiteButton>
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Close banner"
            className="hidden size-11 shrink-0 place-items-center text-navy transition-colors duration-180 ease-out hover:bg-navy/5 nav:inline-grid"
          >
            <X size={20} aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
}

const ConsentBanner = () => {
  const { showBanner, handleAccept, handleDismiss } = useConsentChoice();

  if (!showBanner) return null;

  if (flag('SLOT_MODEL')) {
    return <SiteConsentBanner onAccept={handleAccept} onDismiss={handleDismiss} />;
  }

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[9999] animate-slide-up mb-16 md:mb-0"
      style={{
        backgroundColor: 'rgba(17, 24, 39, 0.98)',
        backdropFilter: 'blur(10px)',
        boxShadow: '0 -4px 24px rgba(0, 0, 0, 0.2)'
      }}
    >
      <div className="max-w-[1240px] mx-auto px-6 py-4">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          {/* Content */}
          <div className="flex items-start gap-3 flex-1">
            <Cookie size={24} style={{ color: '#FF7A1A', flexShrink: 0, marginTop: '2px' }} />
            <div>
              <p className="text-base font-medium mb-1" style={{
                fontFamily: 'Inter, sans-serif',
                color: '#FFFFFF'
              }}>
                We use cookies to enhance your experience
              </p>
              <p className="text-sm" style={{
                fontFamily: 'Inter, sans-serif',
                color: '#9CA3AF'
              }}>
                This site uses cookies for analytics and improving your experience. By continuing to use this site, you agree to our use of cookies.{' '}
                <Link
                  to="/privacy"
                  className="underline hover:text-orange-400 transition-colors duration-200"
                  style={{ color: '#FF7A1A' }}
                >
                  Learn more
                </Link>
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 w-full md:w-auto">
            <Button
              onClick={handleDismiss}
              variant="outline"
              className="flex-1 md:flex-none rounded-full px-6 py-2 min-h-[44px] text-sm font-medium border-2 transition-all duration-200"
              style={{
                borderColor: '#475569',
                color: '#9CA3AF',
                backgroundColor: 'transparent'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#FF7A1A';
                e.currentTarget.style.color = '#FF7A1A';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#475569';
                e.currentTarget.style.color = '#9CA3AF';
              }}
            >
              Dismiss
            </Button>
            <Button
              onClick={handleAccept}
              className="flex-1 md:flex-none rounded-full px-6 py-2 min-h-[44px] text-sm font-semibold transition-all duration-200"
              style={{
                backgroundColor: '#FF7A1A',
                color: '#FFFFFF'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#E66A0A';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#FF7A1A';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              Accept
            </Button>
            <button
              onClick={handleDismiss}
              className="p-2 rounded-full hover:bg-gray-800 transition-colors duration-200 hidden md:block"
              aria-label="Close banner"
            >
              <X size={20} color="#9CA3AF" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsentBanner;
